import type { CityConfig } from "../city-template/types";

export const ontarioConfig: CityConfig = {
  city: "Ontario",
  state: "California",
  stateAbbr: "CA",
  slug: "ontario",
  route: "/ontario",
  title: "Emergency Passport Ontario, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Ontario, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Bernardino County and Inland Empire travelers near Ontario International Airport. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Ontario, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Ontario and Inland Empire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Ontario%20California%20cityscape%20at%20golden%20hour%20with%20modern%20suburban%20skyline%20and%20Ontario%20International%20Airport%20in%20the%20background%2C%20shopping%20district%20with%20palm%20trees%20and%20wide%20boulevards%2C%20San%20Gabriel%20Mountains%20visible%20on%20the%20horizon%20with%20warm%20sunset%20light%2C%20clean%20modern%20planned%20city%20environment%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20suburban%20atmosphere&width=1800&height=700&seq=ontario-hero-001&orientation=landscape",
  heroImageAlt: "Ontario California skyline at golden hour with airport and mountain backdrop",
  geoPosition: "34.0633;-117.6509",
  geoPlacename: "Ontario, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 55–70 minutes from Ontario via I-10",
  },
  nearbyCitiesZips: [
    { name: "Ontario", zips: "91761, 91762, 91764" },
    { name: "Rancho Cucamonga", zips: "91701, 91730, 91737, 91739" },
    { name: "Upland", zips: "91784, 91785, 91786" },
    { name: "Claremont", zips: "91711" },
    { name: "Montclair", zips: "91763" },
    { name: "Chino", zips: "91708, 91710" },
    { name: "Pomona", zips: "91766, 91767, 91768, 91769" },
    { name: "Fontana", zips: "92335, 92336, 92337" },
    { name: "Riverside", zips: "92501–92509, 92512–92519, 92521" },
    { name: "Corona", zips: "92877, 92878, 92879, 92880, 92881, 92882, 92883" },
    { name: "Chino Hills", zips: "91709" },
    { name: "San Bernardino", zips: "92401–92411, 92415" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Inland Empire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/los-angeles" },
    { name: "Riverside", route: "/riverside" },
    { name: "San Diego", route: "/san-diego" },
    { name: "Anaheim", route: "/anaheim" },
    { name: "Pasadena", route: "/pasadena" },
    { name: "Santa Ana", route: "/santa-ana" },
    { name: "Long Beach", route: "/long-beach" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Ontario remotely via secure courier. We do not have a walk-in office in Ontario — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Inland Empire travelers.",
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, CA 90071",
  fpcOfficeDistance: "Serving all of San Bernardino County",
  serviceAreaIntro: "From Ontario's bustling commercial district near Ontario Mills to the residential communities of Rancho Cucamonga, from Upland to the historic streets of Claremont — Fast Passport Center serves the western San Bernardino County community. We regularly help travelers from Chino, Pomona, Montclair, and Fontana get their passports fast.",
  nearbyCitiesIntro: "We serve all Ontario neighborhoods and nearby Inland Empire cities including Rancho Cucamonga, Upland, Claremont, Chino, Pomona, Fontana, and Riverside. If you are in western San Bernardino County or nearby Riverside County, we have you covered.",
  howItWorksIntro: "Most Ontario travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Ontario International Airport being a growing international gateway, Ontario travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Ontario and Inland Empire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Ontario? Here are the most common ones we hear from Inland Empire travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Inland Empire international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Alex V.",
      location: "Ontario, CA",
      rating: 5,
      text: "My flight to Taiwan was in 2 days and my passport had expired. Fast Passport Center got me a new one in 30 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia W.",
      location: "Rancho Cucamonga, CA",
      rating: 5,
      text: "Needed to renew my passport for a family vacation to Hawaii. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "James H.",
      location: "Upland, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Cancun. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Nancy C.",
      location: "Chino, CA",
      rating: 5,
      text: "As a frequent international traveler for work from the Inland Empire, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "ontario-1",
      name: "Ontario Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1350 W Holt Blvd",
      city: "Ontario",
      state: "CA",
      zip: "91762",
      phone: "(909) 984-0314",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.7 miles from Ontario Mills",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "ontario-2",
      name: "Rancho Cucamonga Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10905 East Foothill Blvd",
      city: "Rancho Cucamonga",
      state: "CA",
      zip: "91730",
      phone: "(909) 980-4714",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from Victoria Gardens",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "ontario-3",
      name: "Upland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "333 E Arrow Hwy",
      city: "Upland",
      state: "CA",
      zip: "91786",
      phone: "(909) 985-2684",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Upland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern San Bernardino County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "ontario-4",
      name: "Chino Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5495 Philadelphia St",
      city: "Chino",
      state: "CA",
      zip: "91710",
      phone: "(909) 627-4341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Chino",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern San Bernardino County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "ontario-5",
      name: "Claremont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 N Indian Hill Blvd",
      city: "Claremont",
      state: "CA",
      zip: "91711",
      phone: "(909) 624-5394",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from the Claremont Colleges",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for college students and faculty. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "ontario-6",
      name: "San Bernardino County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "222 W Hospitality Ln",
      city: "San Bernardino",
      state: "CA",
      zip: "92415",
      phone: "(909) 387-8306",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "15 miles from Ontario",
      website: "https://www.sbcounty.gov/",
      websiteLabel: "SB County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "ONT Airport Hub", agency: "No airport hub expertise", fpc: "Ontario International Airport, logistics, and Inland Empire business travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Bernardino County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Ontario applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Inland Empire families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Inland Empire travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Ontario." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Ontario address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Ontario before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Ontario for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Inland Empire travelers." },
    { q: "How fast can I renew my passport in Ontario?", a: "Fast Passport Center offers fast passport renewal in Ontario in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Ontario in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Ontario?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Ontario residents." },
    { q: "Can I expedite a child passport in Ontario?", a: "Yes. Fast Passport Center expedites child passports in Ontario for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Ontario with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Ontario?",
      a: "With Fast Passport Center's emergency courier service, Ontario travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Ontario?",
      a: "Popular options include the Ontario Post Office (91762), Rancho Cucamonga Post Office (91730), Upland Post Office (91786), and Chino Post Office (91710). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Ontario?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live near Ontario International Airport?",
      a: "We serve all of western San Bernardino County and nearby Riverside County including Ontario, Rancho Cucamonga, Upland, Claremont, Chino, and Pomona. Our overnight shipping labels work from any UPS location, so your location near the airport doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Ontario?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Ontario?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Ontario, Rancho Cucamonga, and Upland offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Ontario?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Ontario?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Inland Empire families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Ontario", address: "2325 S Grove Ave, Ontario, CA 91761", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Rancho Cucamonga", address: "12749 N Mainstreet, Rancho Cucamonga, CA 91739", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Ontario", address: "4320 E Mills Cir, Ontario, CA 91764", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Upland", address: "222 N Mountain Ave, Upland, CA 91786", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Chino", address: "3943 Grand Ave, Chino, CA 91710", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Claremont", address: "1250 N Indian Hill Blvd, Claremont, CA 91711", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Fontana", address: "15142 Summit Ave, Fontana, CA 92336", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Rancho Cucamonga", address: "12530 Monte Vista Ave, Rancho Cucamonga, CA 91739", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Ontario",
      address: "2855 E Philadelphia St, Ontario, CA 91761",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Rancho Cucamonga",
      address: "9037 Arrow Rte, Rancho Cucamonga, CA 91730",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Pomona",
      address: "1600 S Garey Ave, Pomona, CA 91766",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Chino",
      address: "14677 Ramona Ave, Chino, CA 91710",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for southern San Bernardino County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "3 miles from downtown Ontario",
      address: "2900 E Airport Dr, Ontario, CA 91761",
      note: "Major commercial airport for the Inland Empire. International flights to Mexico, Canada, and Taiwan. Growing hub for domestic and international travel.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "45 miles from Ontario",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-10.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "35 miles from Ontario",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "Commercial airport for Orange County. International flights to Mexico, Canada, and domestic connections.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [{ icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from California CDPH Vital Records for San Bernardino County.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the San Bernardino County Clerk-Recorder.", url: "https://www.sbcounty.gov/" }, { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through SB County Superior Court.", url: "https://www.sb-court.org/" }],
};